// src/admin/screens/login/EnrolTotp.tsx
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@admin/lib/supabase';
import { useAuth } from '@admin/auth/useAuth';
import styles from './Login.module.css';

const schema = z.object({ code: z.string().regex(/^\d{6}$/, '6 digits') });
type FormValues = z.infer<typeof schema>;

export default function EnrolTotp() {
  const { refresh, user } = useAuth();
  const navigate = useNavigate();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { register, handleSubmit, formState: { isSubmitting } } =
    useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    void (async () => {
      // Clean up any unverified factors first
      const list = await supabase.auth.mfa.listFactors();
      for (const f of list.data?.totp ?? []) {
        if (f.status !== 'verified') await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Thandizo admin (${user?.email ?? 'staff'})`,
      });
      if (error) { setErr(error.message); return; }
      setFactorId(data.id);
      setQrSvg(data.totp.qr_code);
      setSecret(data.totp.secret);
    })();
  }, [user?.email]);

  const onSubmit = async ({ code }: FormValues) => {
    if (!factorId) return;
    setErr(null);
    const ch = await supabase.auth.mfa.challenge({ factorId });
    if (ch.error) { setErr(ch.error.message); return; }
    const v = await supabase.auth.mfa.verify({ factorId, challengeId: ch.data.id, code });
    if (v.error) { setErr(v.error.message); return; }
    await refresh();
    navigate('/', { replace: true });
  };

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h1>Enrol two-step</h1>
        <p className="small">Scan this QR with Google Authenticator or 1Password, then enter the 6-digit code.</p>
        {qrSvg && <div dangerouslySetInnerHTML={{ __html: qrSvg }} aria-label="TOTP QR" />}
        {secret && <p className="small" style={{ fontFamily: 'monospace' }}>Or enter: {secret}</p>}
        <label>
          Code
          <input inputMode="numeric" autoFocus maxLength={6} {...register('code')} />
        </label>
        {err && <div className={styles.error}>{err}</div>}
        <button type="submit" className={styles.submit} disabled={isSubmitting || !factorId}>
          {isSubmitting ? 'Verifying…' : 'Enable'}
        </button>
      </form>
    </div>
  );
}
